<template>
    <template
        v-if="
            (reverse && storyIndex == 1 && !hasText) ||
            (!reverse && storyIndex == 2 && hasText && flipped) ||
            (!reverse && storyIndex == 1 && !hasText && !flipped)
        "
    >
        <svg
            ref="arrow"
            xmlns="http://www.w3.org/2000/svg"
            xml:space="preserve"
            :class="[themeClass, reverse && storyIndex == 1 && !hasText ? '--flipped-arrow-a' : '']"
            style="
                fill-rule: evenodd;
                clip-rule: evenodd;
                stroke-linecap: round;
                stroke-linejoin: round;
                stroke-miterlimit: 1.5;
            "
            viewBox="0 0 339 77"
        >
            <defs>
                <mask
                    :id="'mask' + maskSuffix"
                    maskUnits="userSpaceOnUse"
                >
                    <path
                        ref="maskPath"
                        d="M478.405 334.631c0 31.06-91.584 19.158-99.643 39.717-7.582 19.339.584 26.309 23.95 33.075 15.132 4.381 78.755-1.672 111.28-11.344 17.106-5.087 91.514-36.362 102.912-39.389 21.232-5.639 88.524-26.244 88.524 42.206"
                        style="fill: none; stroke: var(--arrow-color); stroke-width: 3px; stroke-miterlimit: 2"
                        transform="translate(-374.766 -333.381)"
                    />
                </mask>
            </defs>

            <g :mask="'url(#mask' + maskSuffix + ')'">
                <path
                    d="M478.405 334.631c0 31.06-91.584 19.158-99.643 39.717-7.582 19.339.584 26.309 23.95 33.075 15.132 4.381 78.755-1.672 111.28-11.344 17.106-5.087 91.514-36.362 102.912-39.389 21.232-5.639 88.524-26.244 88.524 42.206"
                    style="
                        fill: none;
                        stroke: var(--arrow-color);
                        stroke-width: 3px;
                        stroke-miterlimit: 2;
                        stroke-dasharray: 8, 8, 0, 0, 0, 0;
                    "
                    transform="translate(-374.766 -333.381)"
                />
            </g>

            <g
                ref="arrowHead"
                style="
                    opacity: 0;
                    transform: translateY(-8px);
                    transition:
                        transform 0.8s ease,
                        opacity 0.8s ease;
                "
            >
                <path
                    d="m449.429 312.79 17.183 34.366h-34.366l17.183-34.366Z"
                    style="fill: var(--arrow-color); stroke: var(--arrow-color); stroke-width: 13.63px"
                    transform="rotate(180 239.507 88.645) scale(.33009)"
                />
            </g>
        </svg>
    </template>

    <template v-else>
        <svg
            ref="arrow"
            class="--reverse"
            xmlns="http://www.w3.org/2000/svg"
            xml:space="preserve"
            :class="[themeClass, storyIndex == 1 || (storyIndex == 2 && !flipped) ? '--flipped-arrow-b' : '']"
            style="
                fill-rule: evenodd;
                clip-rule: evenodd;
                stroke-linecap: round;
                stroke-linejoin: round;
                stroke-miterlimit: 1.5;
            "
            viewBox="0 0 220 77"
        >
            <defs>
                <mask
                    :id="'mask' + maskSuffix"
                    maskUnits="userSpaceOnUse"
                >
                    <path
                        ref="maskPath"
                        d="M619.904 333.964c0 2.269.369 10.648-4.327 12.443-39.19 14.987-101.788 3.605-116.85 27.941-6.947 11.224-4.8 21.18 13.399 21.403 28.55.349 37.596-3.261 53.901-7.746 18.813-5.175 12.619-3.841 43.684-13.657 2.735-.864 5.975-1.615 9.582-2.23 30.21-5.148 86.135-.739 86.135 26.778"
                        style="fill: none; stroke: var(--arrow-color); stroke-width: 2.5px; stroke-miterlimit: 1"
                        transform="translate(-493.819 -332.714)"
                    />
                </mask>
            </defs>

            <g :mask="'url(#mask' + maskSuffix + ')'">
                <path
                    d="M619.904 333.964c0 2.269.369 10.648-4.327 12.443-39.19 14.987-101.788 3.605-116.85 27.941-6.947 11.224-4.8 21.18 13.399 21.403 28.55.349 37.596-3.261 53.901-7.746 18.813-5.175 12.619-3.841 43.684-13.657 2.735-.864 5.975-1.615 9.582-2.23 30.21-5.148 86.135-.739 86.135 26.778"
                    style="
                        fill: none;
                        stroke: var(--arrow-color);
                        stroke-width: 2.5px;
                        stroke-miterlimit: 1;
                        stroke-dasharray: 5, 5, 0, 0, 0, 0;
                    "
                    transform="translate(-493.819 -332.714)"
                />
            </g>

            <g
                ref="arrowHead"
                style="
                    opacity: 0;
                    transform: translateY(-8px);
                    transition:
                        transform 0.8s ease,
                        opacity 0.8s ease;
                "
            >
                <path
                    d="m449.429 312.79 17.183 34.366h-34.366l17.183-34.366Z"
                    style="fill: var(--arrow-color); stroke: var(--arrow-color); stroke-width: 8px"
                    transform="rotate(180 179.98 88.979) scale(.33009)"
                />
            </g>
        </svg>
    </template>
</template>

<script setup lang="ts">
    import { gsap } from 'gsap';
    import { DrawSVGPlugin } from 'gsap/dist/DrawSVGPlugin';
    import { ScrollTrigger } from 'gsap/ScrollTrigger';
    type Theme = 'orange' | 'blue' | 'red' | 'green' | 'purple' | 'beige' | '';

    const {
        storyIndex = 1,
        reverse = false,
        theme = '',
        hasText = true,
        flipped = false
    } = defineProps<{
        storyIndex?: number;
        reverse?: boolean;
        theme?: Theme;
        hasText?: boolean;
        flipped?: boolean;
    }>();
    gsap.registerPlugin(ScrollTrigger);
    gsap.registerPlugin(DrawSVGPlugin);

    const maskSuffix = storyIndex;

    const arrow = ref();
    const arrowHead = ref();
    const maskPath = ref();

    const themeClass = computed(() => (theme ? `--theme-${theme}` : ''));

    onMounted(() => {
        gsap.set(maskPath.value, { drawSVG: 0 });

        gsap.to(maskPath.value, {
            scrollTrigger: {
                trigger: arrow.value,
                scrub: 2,
                start: '20% 80%',
                end: 'bottom bottom',
                toggleActions: 'restart pause reverse pause'
            },
            ease: 'linear',
            drawSVG: true
        });

        gsap.to(arrowHead.value, {
            scrollTrigger: {
                trigger: arrow.value,
                scrub: 0.6,
                start: '90% 80%',
                end: 'bottom bottom',
                toggleActions: 'restart pause reverse pause'
            },
            opacity: 1,
            y: 0
        });
    });
</script>

<style lang="scss" scoped>
    @import '~/assets/scss/teaser/teaser-story-arrow.scss';
</style>
